import { type FC, type HTMLProps, useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { Button } from '@wartek-id/button-v1'
import { Sheet, SheetContent, SheetOverlay } from '@wartek-id/sheet-v1'
import { Text } from '@wartek-id/text-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Textarea } from '@wartek-id/textarea'

import type { IInputIndicator } from 'app/PemetaanKompetensiPage/pages/RencanaPengembanganDiri/Main'

interface AddReasonSheetProps extends HTMLProps<HTMLDialogElement> {
  isLoading: boolean
  handleSave: (data: IInputIndicator) => void
  handleClose: Function
  dialogData: IInputIndicator
}

const AddReasonSheet: FC<AddReasonSheetProps> = ({
  open,
  handleSave,
  handleClose,
  isLoading,
  dialogData,
}) => {
  const {
    register,
    reset,
    getValues,
    formState: { isValid },
  } = useForm({ mode: 'onChange' })

  useEffect(() => {
    if (dialogData) {
      reset({ alasan: dialogData.reason, kebutuhan: dialogData.needs })
    }
  }, [dialogData, reset])

  const handleClickSave = () => {
    handleSave({
      ...dialogData,
      reason: getValues('alasan'),
      needs: getValues('kebutuhan'),
    })
    resetForm()
  }

  const resetForm = () => {
    reset({ alasan: '', kebutuhan: '' })
  }

  const handleClickClose = () => {
    resetForm()
    handleClose()
  }

  return (
    <Sheet
      isOpen={open}
      sheetSize="full"
      onClose={isLoading ? null : () => handleClickClose()}
    >
      <SheetOverlay />
      <SheetContent className="max-w-2xl m-auto !rounded-none">
        <div className="flex items-center p-4 w-full border-b shadow-card">
          <Icon onClick={() => handleClickClose()}>close</Icon>
          <Text variant="heading-sm" className="ml-6">
            Pilih Indikator
          </Text>
        </div>
        <form className="p-4 space-y-3">
          <div className="rounded-lg bg-gray-5 p-3">
            <Text variant="body-sm-bold">{dialogData?.content.title}</Text>
            <Text variant="body-sm">{dialogData?.content.description}</Text>
          </div>
          <div className="space-y-1">
            <Text>Alasan</Text>
            <Textarea
              maxLength={150}
              rows={2}
              placeholder="Contoh: Indikator ini sesuai dengan kebutuhan sekolah saya"
              {...register('alasan', { required: true })}
            />
          </div>
          <div className="space-y-1">
            <Text>Apa kebutuhan Anda? (opsional)</Text>
            <Textarea
              rows={3}
              placeholder="Contoh: Saya membutuhkan
              pembelajaran yang praktis terkait indikator ini"
              maxLength={150}
              {...register('kebutuhan')}
            />
          </div>

          <Button
            color="blue"
            disabled={isLoading || !isValid}
            isLoading={isLoading}
            fullWidth
            className="!mt-4"
            onClick={handleClickSave}
            size="lg"
          >
            Pilih Indikator
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  )
}

export default AddReasonSheet
